import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../Layout';
import { listBaskets, getCompetitorData, parseCsvFile, fetchCompetitorData } from '../../services/trackingService';
import 'bootstrap/dist/css/bootstrap.min.css';

/* ──────────────────────────────────────────── */
/* Best Sellers Rank — port of best_sellers.py  */
/* Weekly BSR table with movement indicators    */
/* ──────────────────────────────────────────── */

function BestSellersRank() {
    const [baskets, setBaskets] = useState([]);
    const [selectedBasket, setSelectedBasket] = useState(null);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [csvName, setCsvName] = useState(null);

    const accountId = localStorage.getItem('selectedAccountValue') || '';

    const loadBaskets = useCallback(async () => {
        if (!accountId) return;
        try { const res = await listBaskets(accountId); setBaskets(res.data || []); } catch { }
    }, [accountId]);

    useEffect(() => { loadBaskets(); }, [loadBaskets]);

    const loadData = useCallback(async () => {
        if (!selectedBasket || !accountId || selectedBasket.id === 'csv') return;
        setLoading(true);
        setError('');
        try { const res = await getCompetitorData(selectedBasket.id, accountId); setData(res.data || []); }
        catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to load best sellers data.');
        } finally { setLoading(false); }
    }, [selectedBasket, accountId]);

    useEffect(() => { loadData(); }, [loadData]);

    // CSV upload
    const handleCsvUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setLoading(true);
        setError('');
        try {
            const res = await parseCsvFile(file);
            setData(res.data || []);
            setCsvName(file.name);
            setSelectedBasket({ id: 'csv', name: file.name });
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || 'Error parsing CSV.');
        }
        finally { setLoading(false); }
    };
    const handleClearCsv = () => { setCsvName(null); setSelectedBasket(null); setData([]); };

    const handleFetchData = async () => {
        if (!selectedBasket || selectedBasket.id === 'csv') return;
        if (!accountId) {
            setError('Select an Amazon Ads account before fetching data.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await fetchCompetitorData(selectedBasket.id, accountId);
            const refreshed = await getCompetitorData(selectedBasket.id, accountId);
            setData(refreshed.data || []);
        } catch (err) {
            setError(err?.response?.data?.error || err?.response?.data?.message || err.message || 'Failed to fetch data.');
        } finally {
            setLoading(false);
        }
    };

    // Build BSR table grouped by week
    // Only include weeks where at least one ASIN has rank_value data
    const weeks = [...new Set(
        data.filter(r => r.week && r.rank_value != null).map(r => r.week)
    )].sort((a, b) => a - b);

    // Only include ASINs that have at least one rank_value
    const asins = [...new Set(
        data.filter(r => r.asin && r.rank_value != null).map(r => r.asin)
    )];

    const getMovement = (asin, weekIdx) => {
        if (weekIdx === 0) return null;
        const curr = data.find(r => r.asin === asin && r.week === weeks[weekIdx]);
        const prev = data.find(r => r.asin === asin && r.week === weeks[weekIdx - 1]);
        if (!curr || !prev || !curr.rank_value || !prev.rank_value) return null;
        const diff = prev.rank_value - curr.rank_value;
        if (diff > 0) return { dir: 'up', diff, color: 'text-success' };
        if (diff < 0) return { dir: 'down', diff: Math.abs(diff), color: 'text-danger' };
        return { dir: 'same', diff: 0, color: 'text-muted' };
    };

    const providerHint = useMemo(() => {
        if (!error) return '';
        const normalized = String(error).toLowerCase();
        if (normalized.includes('not subscribed') || normalized.includes('403')) {
            return 'Live provider access is restricted or not subscribed (Error 403). Please verify your RapidAPI subscription or use a historical tracking CSV.';
        }
        if (normalized.includes('too many requests') || normalized.includes('429')) {
            return 'Provider rate limit reached (Error 429). Retry in a few minutes or reduce ASIN volume.';
        }
        if (normalized.includes('external provider failed')) {
            return 'External provider failed for this request. Try again later or use a historical CSV.';
        }
        return '';
    }, [error]);

    return (
        <Layout>
            <div className="container-fluid py-4">
                <h2 className="fw-bold mb-4">Best Sellers Rank</h2>

                {error && <div className="alert alert-danger">{error}</div>}
                {providerHint && <div className="alert alert-warning">{providerHint}</div>}

                <div className="row mb-3">
                    <div className="col-md-4">
                        <select className="form-select" value={selectedBasket?.id || ''}
                            onChange={e => setSelectedBasket(baskets.find(b => b.id === parseInt(e.target.value)) || null)}>
                            <option value="">Select basket...</option>
                            {baskets.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <div className="d-flex align-items-center gap-2">
                            <label className="btn btn-outline-info btn-sm mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-filetype-csv me-1"></i>Upload CSV
                                <input type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            </label>
                            {csvName && (
                                <span className="badge bg-success d-flex align-items-center gap-1">
                                    {csvName}
                                    <i className="bi bi-x-lg" style={{ cursor: 'pointer' }} onClick={handleClearCsv}></i>
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="col-md-2">
                        <button
                            className="btn btn-success btn-sm w-100"
                            onClick={handleFetchData}
                            disabled={loading || !selectedBasket || selectedBasket.id === 'csv'}
                            title="Fetch latest competitor data"
                        >
                            Fetch Data
                        </button>
                    </div>
                </div>

                {loading && <div className="text-center"><div className="spinner-border" /></div>}

                {!loading && selectedBasket && data.length === 0 && !error && (
                    <div className="alert alert-warning">
                        No hay datos para mostrar. Debes ejecutar Fetch Data en Tracking Overview. Si el proveedor falla (403/429), no se generan nuevos datos.
                    </div>
                )}

                {!loading && selectedBasket && data.length > 0 && asins.length === 0 && !error && (
                    <div className="alert alert-info">
                        Los datos cargados no contienen información de Best Sellers Rank para ningún ASIN.
                    </div>
                )}

                {data.length > 0 && asins.length > 0 && (
                    <div className="card">
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead className="table-dark">
                                        <tr>
                                            <th>ASIN</th>
                                            <th>Brand</th>
                                            <th>Sub-category</th>
                                            {weeks.map(w => <th key={w}>Week {w}</th>)}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {asins.map(asin => {
                                            const latest = data.filter(r => r.asin === asin).sort((a, b) => new Date(b.date) - new Date(a.date))[0];
                                            return (
                                                <tr key={asin}>
                                                    <td className="fw-bold">{asin}</td>
                                                    <td>{latest?.brand || '—'}</td>
                                                    <td><small>{latest?.sub_category_name || '—'}</small></td>
                                                    {weeks.map((w, wi) => {
                                                        const row = data.find(r => r.asin === asin && r.week === w);
                                                        const mov = getMovement(asin, wi);
                                                        return (
                                                            <td key={w} className="text-center">
                                                                {row?.rank_value != null ? (
                                                                    <>
                                                                        <span>#{row.rank_value.toLocaleString()}</span>
                                                                        {mov && (
                                                                            <small className={`d-block ${mov.color}`}>
                                                                                {mov.dir === 'up' && `^ ${mov.diff}`}
                                                                                {mov.dir === 'down' && `v ${mov.diff}`}
                                                                                {mov.dir === 'same' && '—'}
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                ) : '—'}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default BestSellersRank;
